import { ArdoqId } from '@ardoq/api-types';
import { CellAsyncSelect, SelectInstance } from '@ardoq/select';
import { t } from '@ardoq/i18n';
import { useRef } from 'react';
import { CellEditor } from './types';
import { useEffectOnce } from '@ardoq/hooks';
import {
  getLoadParentSuggestionsAsync,
  LabelAndValue,
} from '@ardoq/suggestions-api';
import { debounce } from 'lodash';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';

export const ParentCellEditor: CellEditor<ArdoqId> = props => {
  const selectRef = useRef<SelectInstance<ArdoqId>>(null);
  useEffectOnce(() => {
    selectRef.current?.focus();
  });

  const getComponentName = (componentId: ArdoqId) => {
    return enhancedScopeDataOperations.getComponent(
      props.context.scopeData,
      componentId
    )?.name;
  };

  const selectedParentOption = props.value
    ? {
        label: getComponentName(props.value) ?? props.value,
        value: props.value,
      }
    : null;

  const loadOptions = async (
    searchString: string
  ): Promise<LabelAndValue[]> => {
    const currentComponent = props.data;
    const workspaceId = currentComponent?.rootWorkspace;

    const fetchSuggestions = getLoadParentSuggestionsAsync({
      workspaceIds: [workspaceId],
      componentId: currentComponent._id,
    });

    return await fetchSuggestions(searchString);
  };

  const handleValueChange = (componentId: ArdoqId | null) => {
    props.onValueChange(componentId);
    props.api.stopEditing();
  };

  return (
    <CellAsyncSelect
      selectRef={selectRef}
      i18nLocale={props.context.i18nLocale}
      placeholder={t(
        props.context.i18nLocale,
        'inventory:parentCellEditor.searchComponents',
        'Search components'
      )}
      value={selectedParentOption}
      defaultOptions={true}
      loadOptions={debounce(loadOptions, 100, {
        leading: true,
      })}
      onValueChange={handleValueChange}
      defaultMenuIsOpen={true}
      closeMenuOnSelect={true}
      blurInputOnSelect={true}
      openMenuOnFocus={true}
      // Tab belongs to the grid — it commits and moves to the next cell. Left
      // at react-select's default it would select the focused option on the
      // way out, and `handleValueChange` commits that straight to the cell.
      tabSelectsValue={false}
      isClearable
      noOptionsMessage={({ inputValue }) =>
        inputValue === ''
          ? t(
              props.context.i18nLocale,
              'inventory:parentCellEditor.noAvailableParents',
              'No available parent components'
            )
          : t(
              props.context.i18nLocale,
              'inventory:parentCellEditor.noMatchingComponents',
              'No components match your search'
            )
      }
    />
  );
};
